import { Routes, Route, Navigate, useLocation, useNavigate } from 'react-router-dom';
import { Frame, Navigation, Box } from '@shopify/polaris';
import {
  HomeIcon,
  ChartVerticalIcon,
  SettingsIcon,
  DiscountIcon,
  ThemeEditIcon,
  QuestionCircleIcon,
} from '@shopify/polaris-icons';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { NavMenu } from '@shopify/app-bridge-react';
import { DashboardPage } from './pages/DashboardPage';
import { CampaignsPage } from './pages/CampaignsPage';
import { CampaignTypePickerPage } from './pages/CampaignTypePickerPage';
import { CampaignEditorPage } from './pages/CampaignEditorPage';
import { WidgetsPage } from './pages/WidgetsPage';
import { PopupEditorPage } from './pages/widgets/PopupEditorPage';
import { MilestoneEditorPage } from './pages/widgets/MilestoneEditorPage';
import { CongratsEditorPage } from './pages/widgets/CongratsEditorPage';
import { PromoEditorPage } from './pages/widgets/PromoEditorPage';
import { AnalyticsPage } from './pages/AnalyticsPage';
import { SettingsPage } from './pages/SettingsPage';
import { HelpPage } from './pages/HelpPage';
import { OnboardingPage } from './pages/OnboardingPage';
import { DiscountRedirectPage } from './pages/DiscountRedirectPage';
import {
  AppSaveBarHost,
  hideAllSaveBars,
} from './components/ContextualSaveBar';
import { CrispChat } from './components/CrispChat';
import { t } from './i18n';

/**
 * True when running inside Shopify admin (iframe or host/embedded params).
 * Built for Shopify: primary nav must use App Bridge → left bar under the app name.
 */
function useIsEmbeddedInAdmin() {
  return useMemo(() => {
    const p = new URLSearchParams(window.location.search);
    if (p.get('host') || p.get('embedded') === '1') {
      try {
        sessionStorage.setItem('gf_embedded', '1');
        if (p.get('host')) sessionStorage.setItem('gf_host', p.get('host')!);
      } catch {
        /* ignore */
      }
      return true;
    }
    try {
      if (sessionStorage.getItem('gf_embedded') === '1') return true;
    } catch {
      /* ignore */
    }
    // Loaded in an iframe → almost always Shopify admin app iframe
    try {
      if (window.self !== window.top) return true;
    } catch {
      return true;
    }
    return false;
  }, []);
}

const NAV_ITEMS = [
  { path: '/campaigns', label: 'Campaigns' },
  { path: '/widgets', label: 'Widgets' },
  { path: '/analytics', label: 'Analytics' },
  { path: '/settings', label: 'Settings' },
  { path: '/help', label: 'Help' },
] as const;

/**
 * Built for Shopify layout:
 * - No custom top bar
 * - Nav under app name in Shopify left sidebar via App Bridge <NavMenu>
 * - Settings is a left-nav item (same list as Campaigns / Widgets)
 */
export function App() {
  const location = useLocation();
  const navigate = useNavigate();
  const isEmbedded = useIsEmbeddedInAdmin();
  const [mobileNav, setMobileNav] = useState(false);
  const toggleMobile = useCallback(() => setMobileNav((v) => !v), []);

  // Keep shop/host on client navigations so API + embed detection stay stable
  useEffect(() => {
    const p = new URLSearchParams(window.location.search);
    const shop = p.get('shop');
    const host = p.get('host');
    if (shop) {
      try {
        sessionStorage.setItem('gf_shop', shop);
      } catch {
        /* ignore */
      }
    }
    if (host) {
      try {
        sessionStorage.setItem('gf_host', host);
        sessionStorage.setItem('gf_embedded', '1');
      } catch {
        /* ignore */
      }
    }
  }, [location.search]);

  // Clear any orphaned App Bridge host chrome bars on navigation.
  // Form pages re-register with AppSaveBarHost if still dirty.
  useEffect(() => {
    hideAllSaveBars();
    const timers = [0, 50, 150, 400].map((ms) =>
      window.setTimeout(() => hideAllSaveBars(), ms),
    );
    return () => {
      timers.forEach((t) => window.clearTimeout(t));
    };
  }, [location.pathname]);

  const routes = (
    <AppSaveBarHost>
      <Routes>
        <Route path="/" element={<DashboardPage />} />
        <Route path="/campaigns" element={<CampaignsPage />} />
        <Route path="/campaigns/new" element={<CampaignTypePickerPage />} />
        <Route path="/campaigns/new/configure" element={<CampaignEditorPage />} />
        <Route path="/campaigns/:id" element={<CampaignEditorPage />} />
        <Route path="/widgets" element={<WidgetsPage />} />
        <Route path="/widgets/popup" element={<PopupEditorPage />} />
        <Route path="/widgets/milestone" element={<MilestoneEditorPage />} />
        <Route path="/widgets/congrats" element={<CongratsEditorPage />} />
        <Route path="/widgets/promo" element={<PromoEditorPage />} />
        <Route path="/analytics" element={<AnalyticsPage />} />
        <Route path="/settings" element={<SettingsPage />} />
        <Route path="/help" element={<HelpPage />} />
        <Route path="/onboarding" element={<OnboardingPage />} />
        {/* Shopify Discounts list → Freebi campaign (app discount ui.paths) */}
        <Route path="/discounts/:functionId/:id" element={<DiscountRedirectPage />} />
        <Route path="/discounts/:id" element={<DiscountRedirectPage />} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </AppSaveBarHost>
  );

  /**
   * App Bridge nav menu — items render under "Freebi" in Shopify admin left bar
   * (Apps → Freebi → Campaigns, Widgets, Settings, …)
   * Home is rel="home" so it does not duplicate a second Home item.
   */
  const shopifyAdminNav = (
    <NavMenu>
      <a href="/" rel="home">
        {t("Home")}
      </a>
      {NAV_ITEMS.map((item) => (
        <a key={item.path} href={item.path}>
          {t(item.label)}
        </a>
      ))}
    </NavMenu>
  );

  // ── Embedded in Shopify admin ─────────────────────────────────────────
  // No Polaris Frame / top bar / inner sidebar — only Shopify’s left nav
  if (isEmbedded) {
    return (
      <>
        <CrispChat />
        {shopifyAdminNav}
        <Box paddingBlockStart="400" paddingInline="400">
          {routes}
        </Box>
      </>
    );
  }

  // ── Standalone local dev (not in admin iframe) ────────────────────────
  const localNavigation = (
    <Navigation location={location.pathname}>
      <Navigation.Section
        items={[
          {
            label: t("Home"),
            icon: HomeIcon,
            selected: location.pathname === '/',
            onClick: () => navigate('/'),
          },
          {
            label: t("Campaigns"),
            icon: DiscountIcon,
            selected: location.pathname.startsWith('/campaigns'),
            onClick: () => navigate('/campaigns'),
          },
          {
            label: t("Widgets"),
            icon: ThemeEditIcon,
            selected: location.pathname.startsWith('/widgets'),
            onClick: () => navigate('/widgets'),
          },
          {
            label: t("Analytics"),
            icon: ChartVerticalIcon,
            selected: location.pathname.startsWith('/analytics'),
            onClick: () => navigate('/analytics'),
          },
          {
            label: t("Settings"),
            icon: SettingsIcon,
            selected: location.pathname.startsWith('/settings'),
            onClick: () => navigate('/settings'),
          },
          {
            label: t("Help"),
            icon: QuestionCircleIcon,
            selected: location.pathname.startsWith('/help'),
            onClick: () => navigate('/help'),
          },
        ]}
      />
    </Navigation>
  );

  return (
    <Frame
      navigation={localNavigation}
      showMobileNavigation={mobileNav}
      onNavigationDismiss={toggleMobile}
    >
      <CrispChat />
      {routes}
    </Frame>
  );
}
